// components/LegalRepresentatives/AccountRepresentativesTable.js
import { formatDate } from "../service/utils/formatters";

/**
 * Composant Tableau des Représentants Légaux
 * Affiche la liste des représentants légaux d'un compte avec actions
 */
const AccountRepresentativesTable = ({ 
  representatives, 
  loading, 
  onViewDetails,
  onViewManagedAccounts,
  onViewDocuments,
  onRemoveRepresentative
}) => {
  
  /**
   * Obtenir la configuration du badge de statut
   * @param {string} status - Statut du mandat
   * @returns {Object} Configuration du badge
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { 
        class: 'bg-success bg-opacity-10 text-success border border-success border-opacity-25', 
        label: 'Actif' 
      },
      'INACTIVE': { 
        class: 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25', 
        label: 'Inactif' 
      },
      'PENDING': { 
        class: 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25', 
        label: 'En attente' 
      },
      'EXPIRED': { 
        class: 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25', 
        label: 'Expiré' 
      }
    };
    
    const config = statusConfig[status] || { class: 'bg-secondary', label: status };
    return (
      <span className={`badge rounded-pill ${config.class}`}>
        {config.label}
      </span>
    );
  };

  /**
   * Obtenir la configuration du badge de type de mandat
   * @param {string} mandateType - Type de mandat
   * @returns {JSX.Element} Badge formaté
   */
  const getMandateTypeBadge = (mandateType) => {
    const typeConfig = {
      'TUTORSHIP': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-4',
      'CURATORSHIP': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-4',
      'POWER_OF_ATTORNEY': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-4',
      'LEGAL_GUARDIAN': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-4',
      'parent': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-5',
      'Tuteur': 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-5'
    };
    
    return (
      <span className={`badge rounded-5 ${typeConfig[mandateType] || 'bg-white bg-opacity-10 text-success border border-white border-opacity-25 fs-5'}`}>
        {mandateType}
      </span>
    );
  };

  // État de chargement
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-2 text-muted">Chargement des représentants légaux...</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped align-middle">
        <thead className="table-light">
          <tr>
            <th>Représentant</th>
            <th>Email</th>
            <th>Type de Mandat</th>
            <th>Relation</th>
            <th>Statut</th>
            <th>Début Mandat</th>
            <th>Fin Mandat</th>
            <th>Date Création</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {representatives.map((representative) => (
            <tr key={representative.id}>
              <td>
                <div className="fw-semibold">
                  {representative.representativeDisplayName || representative.representativeEmail}
                </div>
              </td>
              <td>
                <a 
                  href={`mailto:${representative.representativeEmail}`} 
                  className="text-decoration-none"
                  title="Envoyer un email"
                >
                  {representative.representativeEmail}
                </a>
              </td>
              <td>
                {getMandateTypeBadge(representative.mandateType)}
              </td>
              <td>{representative.relationshipType}</td>
              <td>
                {getStatusBadge(representative.status)}
              </td>
              <td>
                <small className="text-muted">
                  {formatDate(representative.startDate)}
                </small>
              </td>
              <td>
                <small className="text-muted">
                  {formatDate(representative.endDate)}
                </small>
              </td>
              <td>
                <small className="text-muted">
                  {formatDate(representative.creationDate)}
                </small>
              </td>
              <td className="text-center">
                <div className="btn-group btn-group-sm" role="group">
                  {/* Bouton Voir les détails */}
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => onViewDetails(representative)}
                    title="Voir les détails"
                    data-bs-toggle="tooltip"
                  >
                    <i className="fas fa-eye"></i>
                  </button>
                  
                  {/* Bouton Voir les comptes gérés */}
                  <button
                    className="btn btn-outline-info"
                    onClick={() => onViewManagedAccounts(representative)}
                    title="Voir les comptes gérés"
                    data-bs-toggle="tooltip"
                  >
                    <i className="fas fa-list"></i>
                  </button>
                  
                  {/* NOUVEAU BOUTON : Voir les documents */}
                  <button
                    className="btn btn-outline-warning"
                    onClick={() => onViewDocuments(representative)}
                    title="Voir les documents"
                    data-bs-toggle="tooltip"
                  >
                    <i className="fas fa-file-alt"></i>
                  </button>

                  {/* // Dans le bouton de dissociation */}
                  {representative.status == 'ACTIVE' && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => {
                        onRemoveRepresentative(representative);
                      }}
                      title="Dissocier ce mandataire"
                      data-bs-toggle="tooltip"
                    >
                      <i className="fas fa-user-minus"></i>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AccountRepresentativesTable;